import * as amplitude from '@amplitude/unified';
import { supabase } from './supabase';

const AMPLITUDE_API_KEY = (import.meta as any).env.VITE_AMPLITUDE_API_KEY;
const getApiKey = () => AMPLITUDE_API_KEY || 'bf23483d05773ea3d01156fa39597ba9';

/**
 * Initialize Amplitude Analytics
 */
export const initAnalytics = () => {
  const apiKey = getApiKey();
  
  if (!apiKey) {
    console.warn('Amplitude API Key not found. Analytics disabled.');
    return;
  }

  amplitude.initAll(apiKey, {
    analytics: {
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: {
          thrashedCursor: true,
          errorClicks: true,
          deadClicks: true,
          rageClicks: true
        }
      }
    },
    sessionReplay: {
      sampleRate: 1
    }
  });
  
  console.info('[MoniFlow] Analytics & Session Replay Initialized');
};

/**
 * Identify User
 */
export const identifyUser = (userId: string, userProperties?: Record<string, any>) => {
  if (!getApiKey()) return;
  
  amplitude.setUserId(userId);
  if (userProperties) {
    const identifyObj = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => {
      identifyObj.set(key, value);
    });
    amplitude.identify(identifyObj);
  }
};

/**
 * Clear User Identity (Logout)
 */
export const resetAnalytics = () => {
    if (!getApiKey()) return;
    amplitude.reset();
};

/**
 * Track Event
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (!getApiKey()) return;
  amplitude.track(eventName, properties);
  
  // Persist critical storefront events to Supabase for merchant dashboards
  if (eventName === ANALYTICS_EVENTS.STOREFRONT_VIEWED && properties?.seller_id) {
    supabase.from('store_views').insert({
      seller_id: properties.seller_id,
      utm_source: properties.utm_source || sessionStorage.getItem('moniflow_utm_source'),
      utm_medium: properties.utm_medium || sessionStorage.getItem('moniflow_utm_medium'),
      utm_campaign: properties.utm_campaign || sessionStorage.getItem('moniflow_utm_campaign'),
      visitor_id: localStorage.getItem('moniflow_visitor_id') || undefined
    }).then(({ error }) => {
      if (error) console.error('[Analytics] DB Persistence Error:', error);
    });
  }

  // Debug log for development
  if ((import.meta as any).env.DEV) {
    console.debug(`[Analytics] ${eventName}`, properties);
  }
};

/**
 * Standard Events Constants
 */
export const ANALYTICS_EVENTS = {
    // Seller Actions
    ORDER_CREATED_MANUALLY: 'Seller Order Created',
    BROADCAST_SENT: 'Broadcast Sent',
    PRODUCT_ADDED: 'Product Added',
    PRODUCT_EDITED: 'Product Edited',
    PRODUCT_DELETED: 'Product Deleted',
    PAYMENT_CONFIRMED: 'Payment Confirmed',
    ORDER_STATUS_UPDATED: 'Order Status Updated',
    ORDERS_BULK_DELETED: 'Orders Bulk Deleted',
    REMINDER_SENT: 'Reminder Sent',
    BUG_REPORT_SUBMITTED: 'Bug Report Submitted',
    CONTACT_SUPPORT_CLICKED: 'Contact Support Clicked',
    STORE_SHARED: 'Store Shared',
    SUBSCRIPTION_UPGRADED: 'Subscription Upgraded',
    EXPENSE_ADDED: 'Expense Added',
    EXPENSE_DELETED: 'Expense Deleted',
    SMART_SCAN_COMPLETED: 'Smart Scan Completed',
    STATUS_POSTED: 'Status Posted',
    
    // Customer Actions
    STOREFRONT_VIEWED: 'Storefront Viewed',
    CHECKOUT_STARTED: 'Checkout Started',
    CHECKOUT_COMPLETED: 'Checkout Completed',
    WHATSAPP_CHAT_CLICKED: 'WhatsApp Chat Clicked',
    
    // App Lifecycle
    LOGIN: 'Login',
    LOGOUT: 'Logout',
    SIGNUP: 'Sign Up',
};
